import React from "react";
import { formatCOP } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import EntregablesPanel from "../../components/EntregablesPanel";
import { useN2Offer } from "../../lib/useN2Offer";

/**
 * Selector de variante N2 (auto vs reviewed).
 * Fase 12: permite al usuario elegir entre Kit automatizado o con revisión humana.
 */
export function VariantN2Selector({
  variantSelected,
  setVariantSelected,
  variantDisclaimerAccepted,
  setVariantDisclaimerAccepted,
  submittingVariant,
  onSubmit,
}) {
  const n2Offer = useN2Offer();
  return (
    <section
      className="mb-8 border border-primary/30 bg-primary/5 p-6"
      data-testid="variant-n2-selector"
    >
      <h2 className="font-serif text-xl text-gray-900 mb-4">Elige tu plan de Kit Documental</h2>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* AUTO */}
        <label
          className={`border-2 p-5 rounded-sm cursor-pointer transition ${variantSelected === "auto" ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400 bg-white"}`}
          data-testid="variant-n2-card-auto"
        >
          <input
            type="radio"
            name="variant_n2"
            value="auto"
            checked={variantSelected === "auto"}
            onChange={(e) => {
              setVariantSelected(e.target.value);
              setVariantDisclaimerAccepted(false);
            }}
            className="mr-2"
            data-testid="variant-n2-auto"
          />
          <span className="inline-block text-[9px] uppercase bg-amber-50 text-amber-800 border border-amber-300 px-2 py-1 mb-2">
            Sin revisión humana
          </span>
          <div className="font-serif text-lg text-gray-900">Kit Documental automatizado</div>
          <div className="font-serif text-2xl text-gray-900 my-2">{formatCOP(n2Offer.price_auto_cop)}</div>
          <p className="text-xs text-gray-600 mt-2">
            Generación automática por IA. Entrega en 5–10 minutos. Más económico.
          </p>
        </label>

        {/* REVIEWED */}
        <label
          className={`border-2 p-5 rounded-sm cursor-pointer transition ${variantSelected === "reviewed" ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400 bg-white"}`}
          data-testid="variant-n2-card-reviewed"
        >
          <input
            type="radio"
            name="variant_n2"
            value="reviewed"
            checked={variantSelected === "reviewed"}
            onChange={(e) => setVariantSelected(e.target.value)}
            className="mr-2"
            data-testid="variant-n2-reviewed"
          />
          <span className="inline-block text-[9px] uppercase bg-emerald-50 text-emerald-800 border border-emerald-300 px-2 py-1 mb-2">
            Con revisión humana experta
          </span>
          <div className="font-serif text-lg text-gray-900">Kit con revisión humana</div>
          <div className="font-serif text-2xl text-gray-900 my-2">{formatCOP(n2Offer.price_reviewed_cop)}</div>
          <p className="text-xs text-gray-600 mt-2">
            IA + revisión humana experta. Entrega en 48–72 horas hábiles. Mayor seguridad jurídica.
          </p>
        </label>
      </div>

      {variantSelected && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-900 mb-3 text-sm uppercase tracking-widest">
            Qué se entrega
          </h3>
          <EntregablesPanel nivel={variantSelected === "auto" ? "kit" : "kit_revisado"} />
        </div>
      )}

      {variantSelected === "auto" && (
        <div className="border border-amber-300 bg-amber-50 p-4 mb-4">
          <label className="flex items-start gap-3 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={variantDisclaimerAccepted}
              onChange={(e) => setVariantDisclaimerAccepted(e.target.checked)}
              className="mt-1"
              data-testid="disclaimer-n2-auto-checkbox"
            />
            <span>
              <strong>Entiendo y acepto:</strong> El Kit Documental será generado por IA sin
              revisión humana experta. JustSapiens Procure NO garantiza la corrección legal del
              contenido. Asumo total responsabilidad por su uso. Los documentos generados son
              insumos de apoyo y no reemplazan el concepto de un abogado titulado ni la decisión
              del ordenador del gasto.
            </span>
          </label>
        </div>
      )}

      <Button
        onClick={onSubmit}
        disabled={
          submittingVariant ||
          !variantSelected ||
          (variantSelected === "auto" && !variantDisclaimerAccepted)
        }
        className="rounded-sm h-12 px-8"
        data-testid="variant-n2-submit"
      >
        {submittingVariant ? "Procesando…" : "Continuar a la información del Kit"}
      </Button>
    </section>
  );
}
